/**
 * Global Features Entity Schemas
 *
 * Canonical Zod schema for the `globalFeatures` portion of the app configuration.
 * Global features are app-wide UI elements and functionality that can be
 * enabled/disabled and configured per school (chat, the floating action button,
 * attendance, the directory role-label presentation, the tab-bar style, and the
 * resolved media-quality policy).
 *
 * This is the SINGLE source of truth — the backend validators, the mobile app,
 * and the web dashboard all import from here. It is stored separately in the
 * database (`SystemConfiguration` category `GLOBAL_FEATURES`, key
 * `GLOBAL_FEATURE_CONFIGS`) and merged into app config at runtime.
 *
 * Note: super-admins always have implicit access to all features.
 *
 * Reconciled to the backend shape (every stored config satisfies it): base
 * config is strict (`visibleForRoles` is a real `UserRole[]`, `status` is the
 * enum), attendance is the nested `defaultBehavior` + `levelConfigs` form, and
 * `canInitiateChatWith` is a fixed-key rules object.
 */

import { z } from 'zod'
import { MediaQualityPolicySchema } from './media-quality-policy'
import { UserRoleSchema } from './navigation'

// ============================================================================
// Base Types
// ============================================================================

/**
 * Base configuration for all global features
 * All global features share these common properties for consistent access control
 */
export const BaseGlobalFeatureConfigSchema = z.object({
  /** Whether this feature is enabled */
  enabled: z.boolean(),

  /**
   * Roles that can see this feature (required)
   *
   * Three visibility modes:
   * - ['student', 'parent', 'faculty', 'admin'] - All roles (explicit list)
   * - ['faculty', 'admin'] - Specific roles only
   * - [] - No roles (feature enabled but not visible - useful for testing/configuration)
   *
   * Note: Super-admins always have implicit access regardless of this setting
   */
  visibleForRoles: z.array(UserRoleSchema),

  /**
   * Restrict feature to beta users only (for gradual rollout)
   * When true, only users marked as beta testers can access this feature
   */
  betaOnly: z.boolean().optional(),

  /**
   * Feature lifecycle status
   * Controls feature rollout and visibility
   */
  status: z.enum(['draft', 'preview', 'beta', 'released']).optional()
})

/**
 * A single global feature as returned/accepted by the by-name admin endpoints
 * (`GET`/`PATCH /system/app-config/global-features/:featureName`). The base
 * fields are typed, but feature-specific keys (chat's `maxGroupSize`,
 * attendance's `levelConfigs`, `tabBar`'s `style`, …) and any custom keys are
 * PRESERVED via `.passthrough()` — the endpoint is generic-by-name and must not
 * strip the very fields it's editing. The root `GlobalFeaturesConfigSchema`
 * stays strict per-feature; this permissive shape is only for the single-feature
 * contracts.
 */
export const SingleGlobalFeatureConfigSchema = BaseGlobalFeatureConfigSchema.passthrough()

// ============================================================================
// Chat Feature Configuration
// ============================================================================

/**
 * The configurable chat roles used by the mobile permission gate: the four
 * persona roles plus the contextual overlay categories (`moderator`,
 * `section_parent`).
 *
 * `admin` is deliberately NOT here — admins always have full access via code, so
 * they are not one of the configurable initiator personas. `moderator` and
 * `section_parent` are *recipient-only* overlays (nobody initiates a chat *as* a
 * moderator or as a section-parent persona), but they live in this union so the
 * mobile gate (`use-chat-permissions.ts`) can reference them as valid recipient
 * categories. See `ChatRecipientRoleSchema` for the enum that validates stored
 * `canInitiateChatWith` recipient arrays.
 */
export const ChatRoleSchema = z.enum(['student', 'parent', 'faculty', 'staff', 'moderator', 'section_parent'])

/**
 * A chat role, or the special `'all'` value that allows chatting with everyone.
 */
export const ChatRoleOrAllSchema = z.union([ChatRoleSchema, z.literal('all')])

/**
 * The recipient categories a `canInitiateChatWith` rule array may list.
 *
 * This is a STRICT SUPERSET of the persona recipient roles accepted before
 * (`parent, student, faculty, staff, admin` — the shared `UserRole` set) PLUS
 * the contextual overlay categories (`moderator`, `section_parent`). It is
 * INTENTIONALLY chat-scoped and decoupled from the shared `UserRoleSchema`: both
 * are chat-only recipient concepts and must NOT leak into navigation /
 * screen-visibility / school-users, which key off `UserRoleSchema`.
 *
 * `admin` is retained on purpose — repointing the rules array at an enum that
 * omitted it would be a NARROWING that silently fail-parses (and thus drops) any
 * stored config listing `admin` as a recipient. This change is additive only: a
 * config without `moderator` / `section_parent` parses and behaves exactly as
 * before.
 */
export const ChatRecipientRoleSchema = z.enum([
  'parent',
  'student',
  'faculty',
  'staff',
  'admin',
  'moderator',
  'section_parent'
])

/**
 * Chat initiation rules schema
 * Maps each role to an array of roles they can initiate chats with
 * Use ['all'] to allow a role to chat with anyone
 */
export const ChatInitiationRulesSchema = z.object({
  student: z.union([z.array(ChatRecipientRoleSchema), z.tuple([z.literal('all')])]).optional(),
  parent: z.union([z.array(ChatRecipientRoleSchema), z.tuple([z.literal('all')])]).optional(),
  faculty: z.union([z.array(ChatRecipientRoleSchema), z.tuple([z.literal('all')])]).optional(),
  staff: z.union([z.array(ChatRecipientRoleSchema), z.tuple([z.literal('all')])]).optional()
})

/**
 * Comprehensive chat feature configuration
 */
export const ChatFeatureConfigSchema = BaseGlobalFeatureConfigSchema.extend({
  /** Show chat bubble on home screen */
  showBubbleOnHome: z.boolean().optional(),

  /** Show chat bubble on feed screen */
  showBubbleOnFeed: z.boolean().optional(),

  /** Show chat bubble on notifications screen */
  showBubbleOnNotifications: z.boolean().optional(),

  /** Show unread message badge in header */
  showHeaderBadge: z.boolean().optional(),

  /** Maximum attachment size in MB */
  maxAttachmentSize: z.number().positive().optional(),

  /** Allowed file types for attachments (MIME types or patterns) */
  allowedFileTypes: z.array(z.string()).optional(),

  /** Maximum number of participants in a group chat */
  maxGroupSize: z.number().positive().optional(),

  /**
   * Chat initiation rules defining which roles can initiate chats with which roles
   * Maps each role to an array of roles they can chat with, or ['all'] for unrestricted access
   *
   * Example:
   * {
   *   "student": ["faculty", "moderator"],
   *   "parent": ["parent", "faculty", "staff"],
   *   "faculty": ["all"],
   *   "staff": ["all"]
   * }
   *
   * Recipient-token scope (ENGINEER NOTE — do not "simplify" the contextual
   * tokens into the persona list): every persona token (`faculty`, `staff`,
   * `parent`, `student`, `admin`) is SCHOOL-WIDE — it admits everyone at the
   * school who holds that role. `moderator` and `section_parent` are the
   * CONTEXTUAL tokens: they admit only people tied to the *initiator's own*
   * roster context, never everyone at the school.
   *   - `moderator` — the effective moderators of the sections and groups that
   *     specific initiator belongs to.
   *   - `section_parent` — the parents who share at least one section with that
   *     specific initiator (plan 2026-07-21-002). A school opts into
   *     roster-scoped parent chat by listing `section_parent` instead of the
   *     school-wide `parent` token in the `parent` rule.
   * Both are recipient-only — nobody initiates a chat *as* a `moderator` or
   * `section_parent` persona, so they are never valid initiator (map) keys, only
   * values in a rule array. The mobile gate resolves these contextual sets
   * server-side per caller; see `use-chat-permissions.ts`.
   */
  canInitiateChatWith: ChatInitiationRulesSchema.optional(),

  /**
   * The grades whose students get chat (SCH-1288): the public IDs of the
   * school's own `z_RefCoreGradeLevels` rows.
   *   - missing — every student, today's behavior;
   *   - `[]` — no student;
   *   - a list — only students whose current grade is on it. A student with no
   *     grade on record is left out.
   * It narrows the student role only: a member who also holds another role the
   * school shows chat to keeps chat through that role. The rule lives in
   * `isChatGradeEligible` (`chat-visibility.ts`). Writes are validated per
   * school in the backend config service; `null` is a write-time "clear" and is
   * never stored.
   */
  studentGradeLevels: z.array(z.string()).optional()
})

/**
 * The chat block as WRITTEN (SCH-1288 KTD9): the stored shape, except that
 * `studentGradeLevels` also takes `null`, a write-time "clear back to All
 * students" the backend honours and never stores. An omitted key keeps the
 * stored list. Every write door (web save, backend update-all body) types its
 * chat block with this, so the two sides are checked against one contract.
 */
export const ChatFeatureConfigWriteSchema = ChatFeatureConfigSchema.extend({
  studentGradeLevels: z.array(z.string()).nullable().optional()
})

// ============================================================================
// Action Button Feature Configuration
// ============================================================================

/**
 * Action button feature configuration
 * Floating action button that appears on multiple screens
 * Uses only base configuration (enabled, visibleForRoles, betaOnly)
 */
export const ActionButtonFeatureConfigSchema = BaseGlobalFeatureConfigSchema

// ============================================================================
// Attendance Feature Configuration
// ============================================================================

/**
 * Attendance reason option for pre-configured absence/tardy reasons
 */
export const AttendanceReasonOptionSchema = z.object({
  /** Unique identifier for the reason */
  id: z.string(),

  /** Display label for the reason */
  label: z.string(),

  /** Whether this is the default selected reason */
  isDefault: z.boolean().optional(),

  /**
   * School-level publicIds (the `levelConfigs` keys) this reason is offered at —
   * READ-TIME INJECTED, never stored.
   *
   * Blackbaud scopes each excuse type to a set of school levels and validates a
   * write against the TARGET STUDENT's level, so a reason a school allowlists
   * school-wide can still be refused for one child (`400 Execuse type not
   * found`). Emitting the level ids lets a picker narrow to what will actually
   * be accepted; the backend enforces it regardless.
   *
   * ⚠️ A level literally named "Entire School" is NOT a wildcard — Blackbaud
   * refuses an excuse type scoped to it for a student at a different level.
   * Every id here is a plain, exact-match level.
   *
   * OPTIONAL and OMITTED (not `[]`) when the type carries no level links at all,
   * so a client can tell "offered nowhere in particular — do not narrow" from a
   * real, narrow scope. That mirrors the server's fail-open on the same fact.
   */
  levelIds: z.array(z.string()).optional(),

  /**
   * Blackbaud's `attendance_type` for this excuse type — `Present`, `Absence` or
   * `Tardy`. READ-TIME INJECTED, never stored.
   *
   * It is what makes the type-appropriateness RULE (plan 2026-08-20-2253)
   * enforceable on the client as well as the server: a `Tardy` type is only ever
   * a late arrival, an `Absence` type only ever an all-day or early dismissal,
   * and a `Present` type is never an absence at all. See
   * {@link isReasonTypeAppropriate} — one rule, shared by both sides, so the
   * picker cannot offer something the endpoint will refuse.
   *
   * A free STRING rather than an enum on purpose: the column mirrors whatever
   * Blackbaud returns, and a value we have not seen must not fail validation of
   * the whole config blob. Unrecognised values fail OPEN in the rule.
   */
  attendanceType: z.string().optional()
})

/**
 * Attendance behavior configuration
 * Controls how attendance works (separate from the data/reasons)
 */
export const AttendanceBehaviorConfigSchema = z.object({
  /** Require notes when taking attendance */
  requireNotes: z.boolean().optional(),

  /** Show bottom sheet for collecting tardy/absent reasons */
  showReasonBottomSheet: z.boolean().optional()
})

/**
 * Per-school-level attendance configuration
 * Contains both behavior settings and filtered reasons for a specific level
 */
export const AttendanceLevelConfigSchema = z.object({
  /** Behavior settings for this school level */
  behavior: AttendanceBehaviorConfigSchema.optional(),

  /** Tardy reasons filtered for this level (injected from database) */
  tardyReasons: z.array(AttendanceReasonOptionSchema).optional(),

  /** Absent reasons filtered for this level (injected from database) */
  absentReasons: z.array(AttendanceReasonOptionSchema).optional()
})

/**
 * Absence types a parent may submit through `POST /v1/students/report-absence`.
 * Mirrors the endpoint's `type` enum — kept as its own schema so the per-type
 * allowlist and the per-type injected reason lists are keyed identically.
 */
export const ParentAbsenceTypeSchema = z.enum(['ALL_DAY', 'LATE_ARRIVAL', 'EARLY_DISMISSAL'])

/** The three request types, in the order the admin surface and the migration report them. */
export const PARENT_ABSENCE_TYPES = ['ALL_DAY', 'LATE_ARRIVAL', 'EARLY_DISMISSAL'] as const satisfies readonly z.infer<
  typeof ParentAbsenceTypeSchema
>[]

/**
 * TYPE APPROPRIATENESS — a RULE, not curation (plan 2026-08-20-2253).
 *
 * Blackbaud stamps every excuse type with an `attendance_type`: `Present`,
 * `Absence` or `Tardy`. Which of them can answer which parent request is a fact
 * about the excuse type, not a school preference, so it stops being a
 * configurable per-absence-type allowlist and becomes this function:
 *
 *   · `Tardy`   → LATE_ARRIVAL only
 *   · `Absence` → ALL_DAY and EARLY_DISMISSAL only
 *   · `Present` → never; a "Present" code is not an absence at all
 *
 * That makes today's live misconfigurations structurally unreachable rather than
 * merely discouraged: Riverstone International (104) allowlists the `Present`
 * type "Attended Class" for all three request types, and Montgomery Bell (88)
 * allowlists both of its Tardy types for all-day and early dismissal.
 *
 * KNOWN, ACCEPTED COST: Montgomery Bell's single deliberate per-type exclusion
 * ("HS Sick" omitted from late arrival) cannot be expressed by a rule and is
 * dropped. It is the only per-type distinction in the entire fleet.
 *
 * FAILS OPEN on an unrecognised or absent `attendanceType` — an absent fact is
 * not a contradiction, and Blackbaud remains the final backstop. `Present` is
 * the one hard "no", because it is a positive statement rather than a missing one.
 *
 * Lives in shared-types so the endpoint and the mobile picker enforce the SAME
 * rule: a client that offers what the server refuses is the dead-end this whole
 * plan exists to close.
 */
export function isReasonTypeAppropriate(
  attendanceType: string | null | undefined,
  type: z.infer<typeof ParentAbsenceTypeSchema>
): boolean {
  if (attendanceType === 'Present') return false
  if (attendanceType === 'Tardy') return type === 'LATE_ARRIVAL'
  if (attendanceType === 'Absence') return type === 'ALL_DAY' || type === 'EARLY_DISMISSAL'
  return true
}

/**
 * The three request types a level curates INDEPENDENTLY: a full-day absence, a
 * late arrival, an early dismissal.
 *
 * The type axis exists because the Blackbaud stamp cannot express the
 * distinction schools ask for: an all-day absence and an early dismissal both
 * draw from the same `Absence` pool, so "Health Excused is fine for a full day
 * but not for leaving early" is unsayable without it. Montgomery Bell Academy
 * had exactly one such exclusion and the 2026-08-20 migration dropped it as an
 * accepted regression; this is the shape that gives it back.
 *
 * Every list optional, and absent means NO reasons for that type (R3). The
 * stamp rule ({@link isReasonTypeAppropriate}) stays a floor BENEATH this: a
 * school cannot allowlist a reason for a type the stamp forbids (R5).
 */
export const ParentAbsenceLevelTypeListsSchema = z.object({
  ALL_DAY: z.array(z.string()).optional(),
  LATE_ARRIVAL: z.array(z.string()).optional(),
  EARLY_DISMISSAL: z.array(z.string()).optional()
})

/**
 * What ONE level stores: either the legacy flat list, or the three per-request-type
 * lists (R1).
 *
 * TWO GENERATIONS, ON PURPOSE, AND THE PARSE MUST STAY LENIENT (KTD4). A flat
 * array reads as the same list for all three types — exactly today's behaviour —
 * so a school that has not migrated is unaffected. It is not decoration: a parse
 * failure resolves to "no stored policy", and with the level check unconditional
 * that REFUSES EVERY SUBMISSION at precisely the schools that configured one.
 * Narrowing this union to the object arm is U8's job, gated on a production read
 * proving no school still stores the flat shape.
 *
 * Each list is independent across levels AND across types (R2): dropping a reason
 * from one level's early-dismissal list touches nothing else. A type key that is
 * absent or empty offers NO reasons (R3) — never all of them, which is the
 * failure direction that lets a parent self-assign an excused code.
 */
export const ParentAbsenceLevelReasonsSchema = z.union([z.array(z.string()), ParentAbsenceLevelTypeListsSchema])

/**
 * STORED per-LEVEL reason allowlist: school-level publicId → the excuse-type
 * publicIds (CUIDs) a parent may pick for a child seated at that level.
 *
 * This is the level axis the per-type allowlist above has never had, and the one
 * shape Blackbaud actually accepts: it scopes every excuse type to a SET of
 * school levels and validates a write against the TARGET CHILD's level, so a
 * school-wide list is a policy its own students cannot always use (Riverstone
 * International School, production 2026-08-20 — ten of twelve configured reasons
 * were invalid for any Elementary student).
 *
 * Each level's list is INDEPENDENT. Fleet-wide, 809 of 1,030 excuse types span
 * two or more levels, so the same reason legitimately appears in several
 * sections — removing it from Elementary must not remove it from Middle.
 *
 * FAIL-CLOSED per level, matching the per-type posture (KTD1): a level with no
 * entry, or an empty list, offers no reasons — never "all reasons".
 *
 * ⚠️ Keys are exact-match level publicIds. A level literally named "Entire
 * School" is NOT a wildcard (Blackbaud refuses a type scoped to it for a student
 * at a different level), and it is also Montgomery Bell Academy's ONLY level,
 * holding 1,038 students. Never special-case a level by NAME.
 *
 * SINCE THE MATRIX (R1) a level stores three lists rather than one — see
 * {@link ParentAbsenceLevelReasonsSchema} for the two stored generations and
 * {@link reasonsForLevelAndType} for the only place they are collapsed.
 */
export const ParentAbsenceReasonsByLevelSchema = z.record(z.string(), ParentAbsenceLevelReasonsSchema)

/**
 * The reasons one level allows for ONE request type, across both stored
 * generations (KTD4).
 *
 * The flat arm is the compatibility answer and the whole reason the union is
 * lenient: a school still on the flat list gets the same list for all three
 * types, which is what it gets today. Callers must go through here rather than
 * reading the record directly, so the two generations are collapsed in exactly
 * one place — the endpoint, the admin surface and the mobile picker all have to
 * agree about what an unmigrated school allows, and three copies of this branch
 * would be three chances to disagree.
 *
 * Returns `[]` for a level that stores an object with that type absent or empty
 * (R3). It never returns "everything".
 */
export function reasonsForLevelAndType(
  levelReasons: z.infer<typeof ParentAbsenceLevelReasonsSchema> | undefined,
  type: z.infer<typeof ParentAbsenceTypeSchema>
): string[] {
  if (!levelReasons) return []
  if (Array.isArray(levelReasons)) return levelReasons
  return levelReasons[type] ?? []
}

/**
 * Every reason a level allows, across all three request types, deduped in stored
 * order.
 *
 * This is the UNION, and it is what a caller that does not know the request type
 * must use — the resolved legacy `resolvedReasonsByLevel` (which old mobile
 * builds read and then narrow by the stamp rule themselves), and any check that
 * runs before the type is known. It is deliberately WIDER than
 * {@link reasonsForLevelAndType}: a caller that HAS the type must use that one,
 * or a school's per-type exclusion silently stops being enforced.
 */
export function allReasonsForLevel(
  levelReasons: z.infer<typeof ParentAbsenceLevelReasonsSchema> | undefined
): string[] {
  if (!levelReasons) return []
  if (Array.isArray(levelReasons)) return levelReasons
  const seen = new Set<string>()
  for (const type of PARENT_ABSENCE_TYPES) {
    for (const id of levelReasons[type] ?? []) seen.add(id)
  }
  return [...seen]
}

/**
 * STORED per-LEVEL request-type allowlist: school-level publicId → the request
 * types (`ALL_DAY` / `LATE_ARRIVAL` / `EARLY_DISMISSAL`) a parent may report for a
 * child seated at that level (SCH-2216, R1).
 *
 * ⚠️ DEFAULT-ALLOW — THE OPPOSITE OF `enabledLevels` AND `reasonsByLevel`, ON
 * PURPOSE. A missing map, a missing level or an empty list means ALL THREE types
 * (R2). The key is opt-in per level, so shipping it changed nothing for any school
 * until an admin set it. Do NOT "harmonise" it to deny-when-unset like its
 * neighbours: that silently turns off late arrivals and early dismissals at every
 * school and level that never configured it. Resolve it only through
 * {@link allowedRequestTypesForLevel}.
 *
 * LENIENT ON READ, STRICT ON WRITE (KTD1, KTD3). Each list is a plain string array
 * with no minimum length and no enum: an unknown string is dropped by the resolver
 * rather than failing the parse, because a parse failure resolves to "no stored
 * policy" and refuses EVERY parent submission at the school. The server write path
 * is where an empty list or an unknown value is refused.
 *
 * Turning a level off entirely is `enabledLevels`' job, never an empty list here
 * (R3). The Blackbaud stamp rule ({@link isReasonTypeAppropriate}) and the per-type
 * reason lists still sit beneath this: a type allowed here with no reasons for it
 * cannot be submitted.
 */
export const ParentAbsenceRequestTypesByLevelSchema = z.record(z.string(), z.array(z.string()))

/**
 * The request types one level allows, in {@link PARENT_ABSENCE_TYPES} order.
 *
 * The only place {@link ParentAbsenceRequestTypesByLevelSchema} is collapsed: the
 * endpoint, the admin page and the mobile picker all call this, so they agree about
 * what an unconfigured level allows. Missing map, missing level, or a list that holds
 * no recognised type → all three (default-allow, R2).
 */
export function allowedRequestTypesForLevel(
  requestTypesByLevel: z.infer<typeof ParentAbsenceRequestTypesByLevelSchema> | undefined,
  levelId: string
): z.infer<typeof ParentAbsenceTypeSchema>[] {
  const stored = requestTypesByLevel?.[levelId]
  const allowed = PARENT_ABSENCE_TYPES.filter(type => stored?.includes(type))
  return allowed.length > 0 ? allowed : [...PARENT_ABSENCE_TYPES]
}

/**
 * READ-TIME INJECTED per-LEVEL reason lists — the stored id lists above resolved
 * against the school's live excuse-type catalog. Never stored:
 * `stripInjectedGlobalFeatureFields` removes this key before any write, exactly
 * as it does for `levelConfigs[*].tardyReasons/absentReasons`.
 *
 * ⚠️ LEGACY SHAPE, STILL EMITTED, AND ITS SHAPE MUST NOT CHANGE (KTD7). Installed
 * mobile builds read this key and cannot be force-upgraded; reshaping it in place
 * leaves them showing an EMPTY picker labelled as deliberate school policy, with
 * no crash and nothing to alert on. The matrix is delivered beside it under
 * {@link ParentAbsenceResolvedReasonsByLevelAndTypeSchema}, never through it.
 */
export const ParentAbsenceResolvedReasonsByLevelSchema = z.record(z.string(), z.array(AttendanceReasonOptionSchema))

/**
 * READ-TIME INJECTED per-LEVEL, per-TYPE reason lists — the stored matrix above
 * resolved against the school's live excuse-type catalog (level → type → labelled
 * options). The ADDITIVE injected key of KTD7: new clients prefer it, old clients
 * never see it, and both agree about an unconfigured school because the resolver
 * omits BOTH resolved keys together rather than emitting an empty one.
 *
 * Never stored — `stripInjectedGlobalFeatureFields` must remove this key too.
 * Missing it there is the defect that froze `media-quality` into school rows.
 */
export const ParentAbsenceResolvedReasonsByLevelAndTypeSchema = z.record(
  z.string(),
  z.object({
    ALL_DAY: z.array(AttendanceReasonOptionSchema).optional(),
    LATE_ARRIVAL: z.array(AttendanceReasonOptionSchema).optional(),
    EARLY_DISMISSAL: z.array(AttendanceReasonOptionSchema).optional()
  })
)

/**
 * Days BEFORE the school-local today a parent may report for when the school has
 * not configured a window. `0` = today is the earliest reportable day (it bounds
 * PAST days only — today itself stays submittable).
 */
export const PARENT_ABSENCE_DEFAULT_DAYS_BACK = 0

/**
 * Days AFTER the school-local today a parent may report for when the school has
 * not configured a window. Matches the endpoint's pre-existing 14-day ALL_DAY
 * span cap, so nothing that works today breaks on the day this ships.
 */
export const PARENT_ABSENCE_DEFAULT_DAYS_FORWARD = 14

/** Upper bounds on the configurable window (guardrails on the guardrail). */
export const PARENT_ABSENCE_MAX_DAYS_BACK = 60
export const PARENT_ABSENCE_MAX_DAYS_FORWARD = 180

/** How many attendance-office recipients a school may save. */
export const PARENT_ABSENCE_MAX_NOTIFY_EMAILS = 10

/**
 * Parent-reported-absence submission policy (plan 2026-08-04-001, KTD5).
 *
 * One sub-object rather than three loose top-level attendance keys, so the whole
 * parent-absence policy reads (and is reasoned about) as a unit. Enforced
 * SERVER-SIDE on both the parent endpoint and the school-admin tester — client
 * hiding is UX, never the gate.
 *
 * ⚠️ THIS OBJECT MUST STAY NON-STRICT. Deleting a key here does NOT delete it
 * from the JSON already stored in `SystemConfiguration` — every school configured
 * before plan PR 4 still physically carries `allowedReasonsByType` in its blob.
 * `z.object` STRIPS unknown keys (it does not reject), and
 * `loadParentAbsencePolicy` treats a parse failure as "no stored policy", which
 * with the level axis unconditional means EVERY parent submission at those schools
 * would be refused. Making this `.strict()` is therefore a production outage, not
 * a tightening. See `packages/shared-types/tests/parent-absence-config.test.ts`.
 */
export const ParentAbsenceConfigSchema = z.object({
  /** Days BEFORE the school-local today a parent may report for. 0 = today is the earliest. */
  submissionDaysBack: z.number().int().min(0).max(PARENT_ABSENCE_MAX_DAYS_BACK).optional(),

  /** Days AFTER the school-local today a parent may report for. */
  submissionDaysForward: z.number().int().min(0).max(PARENT_ABSENCE_MAX_DAYS_FORWARD).optional(),

  /**
   * School-level publicIds parents may report absences FOR (plan
   * 2026-08-20-2253). This is the ONLY reason/eligibility axis since PR 4 deleted
   * the superseded per-absence-type allowlist (`allowedReasonsByType`), so an
   * unset key no longer selects a second policy — it means the school has
   * configured nothing, and FAIL-CLOSED (KTD1) is the answer: no level may report.
   *
   * Still optional because the stored blob is sparse and inherits from the
   * platform row, not because absence picks a different code path. A school that
   * has configured it is enforced FAIL-CLOSED too — a level absent from this list
   * may not report, and `[]` means nobody may.
   *
   * ⚠️ Fail-CLOSED adoption and fail-OPEN resolution are independent axes and must
   * not be collapsed: a child whose level cannot be RESOLVED is ALLOWED through
   * (see the endpoint's level policy). One is a policy the school stated; the
   * other is a fact we are missing.
   */
  enabledLevels: z.array(z.string()).optional(),

  /** Excuse-type publicIds a parent may pick, per school level. See {@link ParentAbsenceReasonsByLevelSchema}. */
  reasonsByLevel: ParentAbsenceReasonsByLevelSchema.optional(),

  /**
   * Request types a parent may report, per school level. DEFAULT-ALLOW, unlike the
   * two keys above. See {@link ParentAbsenceRequestTypesByLevelSchema}.
   */
  enabledRequestTypesByLevel: ParentAbsenceRequestTypesByLevelSchema.optional(),

  /** Attendance-office recipients. Empty/unset ⇒ no notification is sent. */
  notifyEmails: z.array(z.string().email()).max(PARENT_ABSENCE_MAX_NOTIFY_EMAILS).optional(),

  /**
   * INJECTED at read time (never stored): `reasonsByLevel` resolved against the
   * live excuse-type catalog, keyed by the same school-level publicIds.
   *
   * OMITTED entirely (not `{}`) when the school stores no `reasonsByLevel`, so a
   * client can tell "this school has configured no reason policy at all" from
   * "configured one and allows nothing at this level". Both render as an empty
   * picker, but only the second is a statement the school made.
   */
  resolvedReasonsByLevel: ParentAbsenceResolvedReasonsByLevelSchema.optional(),

  /**
   * INJECTED at read time (never stored): `reasonsByLevel` resolved against the
   * live excuse-type catalog and split by request type — the key new clients
   * read (KTD7).
   *
   * OMITTED, together with `resolvedReasonsByLevel`, when the school stores no
   * policy. The two keys are absent or present as a pair on purpose: a client of
   * either generation must reach the same verdict about "this school has
   * configured nothing", or the two answer the same parent differently.
   */
  resolvedReasonsByLevelAndType: ParentAbsenceResolvedReasonsByLevelAndTypeSchema.optional(),

  /**
   * INJECTED at read time (never stored): the window the server will actually
   * enforce, with the defaults above already applied — so no client has to
   * re-implement the fallback.
   */
  resolvedDaysBack: z.number().int().min(0).optional(),
  resolvedDaysForward: z.number().int().min(0).optional()
})

/**
 * Per-school teacher-attendance capability state (plan 2026-08-07-001, D1/KTD1).
 *
 * Three MUTUALLY EXCLUSIVE states, deliberately not two parallel booleans —
 * parallel flags can disagree and then need a precedence rule nobody remembers:
 *
 * - `off`     — no attendance affordance anywhere on the faculty schedule.
 * - `preview` — the roster opens and can be marked, Save is disabled, and the
 *               API rejects a teacher-capability submission. The flow can be
 *               demonstrated on real data with no possibility of a write.
 * - `live`    — an eligible section lead may submit, subject to section
 *               leadership and per-meeting applicability. SCH-1668 removed the
 *               beta-cohort gate here: the attendance write path no longer reads
 *               `betaOnly`, though the field stays on the base schema for the
 *               features that still use it.
 *
 * This is DISTINCT from the base schema's `status` enum, which describes a
 * feature's own development lifecycle rather than a school's capability —
 * repurposing that field would bind every other global feature to attendance's
 * rollout vocabulary (KTD1).
 */
export const TeacherAttendanceStateSchema = z.enum(['off', 'preview', 'live'])

/**
 * Attendance feature configuration
 * Controls attendance-taking functionality and bottom sheet behavior
 */
export const AttendanceFeatureConfigSchema = BaseGlobalFeatureConfigSchema.extend({
  /** Auto-sync attendance with external systems */
  autoSync: z.boolean().optional(),

  /** Default behavior when no level-specific config exists */
  defaultBehavior: AttendanceBehaviorConfigSchema.optional(),

  /** Per-school-level configurations (keyed by level publicId) */
  levelConfigs: z.record(z.string(), AttendanceLevelConfigSchema).optional(),

  /**
   * Capability toggle: allow teachers/faculty to take attendance from the app.
   *
   * Optional + fail-closed by absence (KTD1): read sites treat `undefined` as
   * `false`, so existing stored rows are NOT forced to carry the field and a
   * stale/offline boot payload resolves to "not allowed". Gated under master
   * `enabled` — a capability is active only when `enabled` AND this toggle AND
   * the role in `visibleForRoles` all permit.
   */
  allowTeacherTakeAttendance: z.boolean().optional(),

  /**
   * The per-school teacher-attendance state (R1). Replaces
   * `allowTeacherTakeAttendance`, which stays in the schema as a deprecated
   * read-only field until the mobile build enforcing this one has reached the
   * schools that carry it.
   *
   * FAIL-CLOSED BY ABSENCE (R5): every read site — client and server — treats
   * `undefined`, an unreadable block, and an unrecognized value as `off`. A
   * stale or offline boot payload therefore resolves to "no affordance", never
   * to a write path.
   *
   * NEVER SATISFIED BY INHERITANCE (R23): the server-side enforcement reader
   * (`load-attendance-write-policy.ts`) reads the school's OWN override row and
   * skips the system←school merge, so `preview` and `live` require an explicit
   * per-school value. The fleet default is `off` and no single edit to the
   * global defaults can put a school into a live write path.
   */
  teacherAttendanceState: TeacherAttendanceStateSchema.optional(),

  /**
   * Excuse-type publicIds a TEACHER may pick when marking a class, school-wide
   * (plan 2026-09-11-2339, KTD4). The school's OWN ids from
   * `z_RefAttendanceExcusedTypes` — never Blackbaud platform ids, which differ
   * per school and would silently mean a different type after a re-key.
   *
   * ABSENT and EMPTY are DIFFERENT STATEMENTS, and this is the whole point of
   * the key:
   * - `undefined` — the school has NOT adopted the allow-list. Every live
   *   `Absence` and `Tardy` type is offered and accepted, which is the behaviour
   *   before this key existed. Silent no-op for every school that never sets it.
   * - `[]` — the school HAS adopted it and allows NOTHING. Every exception is
   *   refused, and so is a Present override: an empty list is a decision to take
   *   teachers off the write path, not an unconfigured school.
   *
   * ⚠️ This is the ALLOW-LIST, not the offer. It is enforced SERVER-SIDE in
   * `load-attendance-write-policy.ts` / `update-attendance.ts` against the
   * school's RAW override row, so a stale client build, a hand-rolled request or
   * an inherited platform default cannot widen it. The injected
   * {@link AttendanceFeatureConfigSchema} key `resolvedTeacherReasons` is what a
   * picker renders; this key is what the endpoint obeys.
   *
   * Blackbaud still validates an excuse type against the TARGET STUDENT's level,
   * so an id listed here can still be refused for one child. The level check is
   * a separate axis (`REASON_NOT_ALLOWED_AT_LEVEL`), not a reason to widen this.
   */
  teacherAllowedExcuseTypeIds: z.array(z.string()).optional(),

  /**
   * INJECTED at read time (never stored): `teacherAllowedExcuseTypeIds`
   * intersected with the school's LIVE excuse-type catalog, in the order the
   * school configured, each carrying its label, `attendanceType` and `levelIds`
   * (KTD5). When the allow-list is unset this falls back to every live `Absence`
   * and `Tardy` type — the pre-allow-list offer.
   *
   * The FLAT list new mobile builds read, replacing the level-name matching over
   * `levelConfigs[*].tardyReasons/absentReasons`. Those per-level keys are still
   * emitted (filtered by the allow-list when it is set) for installed builds
   * that cannot be force-upgraded, until the enablement plan's U8 retires them.
   *
   * EMITTED ONLY when the teacher lane is reachable
   * (`teacherAttendanceState !== 'off'` or the deprecated
   * `allowTeacherTakeAttendance`), so a school with no teacher lane ships no
   * teacher offer at all. `[]` means the school allows nothing — a client shows
   * the "ask your school office" notice rather than an empty picker it invented.
   *
   * Never stored: `stripInjectedGlobalFeatureFields` removes this key, exactly
   * as it does for the parent-absence resolved keys. Missing it there is the
   * defect that froze `media-quality` into school rows.
   */
  resolvedTeacherReasons: z.array(AttendanceReasonOptionSchema).optional(),

  /**
   * INJECTED at read time (never stored): the publicId of the school's live
   * `Present` excuse type, the one an explicit Present OVERRIDE row must carry
   * now that `excuse_type_id` is required on every row (KTD3).
   *
   * OMITTED when the school has no live `Present` type — a client that cannot
   * name one cannot offer an override, and the server refuses the row anyway.
   * It is deliberately NOT a member of `resolvedTeacherReasons`: a Present type
   * is not a reason a teacher picks from a list, it is the type the override
   * mechanism sends.
   */
  presentExcuseTypeId: z.string().optional(),

  /**
   * Capability toggle: allow parents to report an absence for their child from
   * the app. Optional + fail-closed by absence (KTD1), same semantics as
   * `allowTeacherTakeAttendance`. The per-student parent-of-student check is a
   * separate, always-on gate.
   */
  allowParentReportAbsence: z.boolean().optional(),

  /**
   * School-configurable guardrails on what a parent may submit (submission
   * window + per-type reason allowlist) and who is emailed when they do.
   *
   * SCHOOL-WIDE, not per-level (plan RD4): every excuse type at the live schools
   * maps to a single `Entire School` level, so a school-wide list is equivalent
   * to today's level-scoped injection. `levelConfigs[*]` stays untouched — the
   * faculty take-attendance path still reads it.
   */
  parentAbsence: ParentAbsenceConfigSchema.optional()
})

// ============================================================================
// Class Grades Feature Configuration
// ============================================================================

/**
 * Class grades (SCH-1783): who may see the SCH-1687 Classes-list percentage and
 * the SCH-1694 per-class grade breakdown.
 *
 * The school's `globalFeatures.classGrades` block selects the audience
 * (`students`, `parents`, `admins`), and the parent audience also requires
 * Blackbaud list-as-parent and parental access. `studentGradeLevels` narrows
 * the students and parents audiences to students in the listed grades.
 *
 * Rules this key carries (plan 2026-09-18-1156):
 *  - Default OFF. Absent block, absent boolean, or anything but `=== true`
 *    reads as off, like `allowTeacherTakeAttendance`.
 *  - `enabled` is the master and means "at least one audience is on".
 *    `enabled: false` denies every audience whatever the booleans say. The
 *    dashboard writes `enabled: students || parents` on every save.
 *  - NEVER put this block in the fleet-default row (`school_id IS NULL`). The
 *    enforcement read (`services/config/class-grades-audience.ts`) parses this
 *    key alone off the school's OWN raw row, so a fleet default could never turn
 *    grades on anyway — but a snapshot save of another feature copies merged
 *    fleet values into the school's row, and then the raw reader would take a
 *    fleet value as the school's choice.
 *  - `visibleForRoles`, `betaOnly` and `status` are inert for this key. They
 *    grant nothing; the reader ignores them.
 *  - The mobile app does no gating of its own: the server omits the percentage
 *    and 404s the breakdown, so the payload is the gate.
 *
 * A school with no block has grades off for both audiences. The old
 * `personDetail.features.classGrades` screen feature is read by nothing
 * (SCH-1783 R17).
 */
export const ClassGradesFeatureConfigSchema = BaseGlobalFeatureConfigSchema.extend({
  /** Students see their own class percentages and breakdowns. Absent ⇒ off. */
  students: z.boolean().optional(),

  /**
   * Parents see their children's class percentages and breakdowns. Absent ⇒ off.
   * A parent also needs Blackbaud list-as-parent AND parental access for that
   * child; this switch never widens that.
   */
  parents: z.boolean().optional(),

  /**
   * School admins (`UsersOnSchools.isAdmin` at this school) see any student's
   * class percentages and breakdowns in the app. Absent ⇒ off. One
   * school-wide switch: `studentGradeLevels` never narrows it.
   */
  admins: z.boolean().optional(),

  /**
   * The grades whose students have class grades shown (SCH-3155): the public
   * IDs of the school's own `z_RefCoreGradeLevels` rows. Same shape and write
   * rules as `chat.studentGradeLevels`.
   *   - missing — every student, today's behavior;
   *   - `[]` — no student;
   *   - a list — only students whose current grade is on it. A student with no
   *     grade on record is left out.
   * It applies to the STUDENT whose grades are read, for the `students` and
   * `parents` audiences only. The `admins` switch is never filtered by it.
   * Enforced in `class-grade-access.ts`. Writes are validated per school in the
   * backend config service; `null` is a write-time "clear" and is never stored.
   */
  studentGradeLevels: z.array(z.string()).optional()
})

/**
 * The classGrades block as WRITTEN: the stored shape, except that
 * `studentGradeLevels` also takes `null`, a write-time "clear back to All
 * grades" the backend honours and never stores. An omitted key keeps the stored
 * list. Mirrors `ChatFeatureConfigWriteSchema`.
 */
export const ClassGradesFeatureConfigWriteSchema = ClassGradesFeatureConfigSchema.extend({
  studentGradeLevels: z.array(z.string()).nullable().optional()
})

// ============================================================================
// Schedule Feature Configuration
// ============================================================================

/**
 * Schedule grade list (SCH-2501): which grades' students have schedules shown.
 *
 * `studentGradeLevels` holds the public IDs of the school's own
 * `z_RefCoreGradeLevels` rows. Same shape and write rules as
 * `classGrades.studentGradeLevels`:
 *   - missing — every student, today's behavior;
 *   - `[]` — every student who has a grade on record is hidden;
 *   - a list — only students whose current grade is on it are shown.
 * A student with no grade on record is always shown. Faculty and staff are
 * never hidden, including a student who is also staff.
 *
 * The list applies to the STUDENT whose schedule it is: hidden for the student,
 * their parents, faculty, the app and the AI schedule tool. The admin dashboard
 * is the one exception.
 *
 * A plain object, not `BaseGlobalFeatureConfigSchema.extend`: the block is
 * school-only and has no fleet-default entry to backfill `enabled` /
 * `visibleForRoles` from. NEVER put this block in the fleet-default row.
 * `null` is a write-time "clear" and is never stored.
 *
 * The one shared rule is `isScheduleHiddenForStudent` in `schedule-visibility.ts`.
 */
export const ScheduleFeatureConfigSchema = z.object({
  studentGradeLevels: z.array(z.string()).optional()
})

/**
 * The schedule block as WRITTEN: the stored shape, except that
 * `studentGradeLevels` also takes `null`, a write-time "clear back to All
 * grades" the backend honours and never stores. An omitted key keeps the stored
 * list. Mirrors `ClassGradesFeatureConfigWriteSchema`.
 */
export const ScheduleFeatureConfigWriteSchema = ScheduleFeatureConfigSchema.extend({
  studentGradeLevels: z.array(z.string()).nullable().optional()
})

// ============================================================================
// Resource Board Feature Configuration
// ============================================================================

/**
 * Resource board layout (SCH-3172): how many tiles per row the mobile resource
 * board renders. One school-wide value, the same on every device.
 *
 *  - `columns` absent ⇒ 1, today's full-width cards. 2 and 3 render compact
 *    tiles (image, title, no description).
 *  - `searchEnabled` (SCH-3231) absent ⇒ no search field.
 *  - `enabled`, `visibleForRoles`, `betaOnly` and `status` are inert for this
 *    key: the board's visibility is the navigation menu's business, and the app
 *    reads `columns` whatever `enabled` says. They exist only so the block fits
 *    the shared base shape and survives the config service's tolerant read.
 *  - The tile ORDER is not here. It lives on `ResourceBoardItem.sortOrder` and
 *    reaches the app already applied, in the resource-board response.
 */
export const ResourceBoardColumnsSchema = z.union([z.literal(1), z.literal(2), z.literal(3)])

export const ResourceBoardFeatureConfigSchema = BaseGlobalFeatureConfigSchema.extend({
  /** Tiles per row on the mobile resource board. Absent ⇒ 1. */
  columns: ResourceBoardColumnsSchema.optional(),
  /**
   * Shows a search field above the board on mobile (SCH-3231). Absent ⇒ off.
   */
  searchEnabled: z.boolean().optional()
})

// ============================================================================
// Call Feature Configuration (Advanced Communications module, SCH-616)
// ============================================================================

/** Retention bounds, in days. Decisions: docs/plans/2026-10-13-001 D4. */
export const CALL_AUDIT_RETENTION_DAYS = { default: 365, min: 90, max: 730 } as const
export const CALL_RECORDING_RETENTION_DAYS = { default: 30, min: 7, max: 180 } as const

/**
 * Call recording settings (SCH-619). Off unless `enabled` is exactly `true`
 * AND an admin acknowledged the consent notice (`acknowledgedAt` set).
 */
export const CallRecordingConfigSchema = z.object({
  enabled: z.boolean(),
  /** Days a recording is kept before the purge job deletes it. Absent ⇒ default. */
  retentionDays: z
    .number()
    .int()
    .min(CALL_RECORDING_RETENTION_DAYS.min)
    .max(CALL_RECORDING_RETENTION_DAYS.max)
    .optional(),
  /** ISO timestamp the consent notice was acknowledged; required to turn recording on. */
  acknowledgedAt: z.string().datetime().optional(),
  /** UsersOnSchools.publicId (or Clerk id for a super-admin) of who acknowledged. */
  acknowledgedBy: z.string().max(191).optional()
})

/**
 * The per-school Advanced Communications calling module (SCH-616), beside
 * `chat`. A PAID module: only the school's OWN override row can turn it on —
 * the server reads it with `services/calls/policy/call-capability.ts`, never
 * through the merged fleet default. `visibleForRoles` lists the roles that may
 * place and receive calls (students also need staff on the other end, see the
 * decisions doc D2). Absent ⇒ off.
 */
export const CallFeatureConfigSchema = BaseGlobalFeatureConfigSchema.extend({
  /** Whether a call may start with the camera available (video). Absent ⇒ true. */
  allowVideo: z.boolean().optional(),
  /** Days call audit metadata is kept. Absent ⇒ `CALL_AUDIT_RETENTION_DAYS.default`. */
  auditRetentionDays: z.number().int().min(CALL_AUDIT_RETENTION_DAYS.min).max(CALL_AUDIT_RETENTION_DAYS.max).optional(),
  /** Recording (SCH-619). Absent ⇒ off. */
  recording: CallRecordingConfigSchema.optional(),
  /** Staff may schedule office-hours sessions (SCH-621). Absent ⇒ true when the module is on. */
  scheduledSessions: z.boolean().optional()
})

// ============================================================================
// Search Feature Configuration
// ============================================================================

/**
 * Feed search in the mobile app (SCH-3189). Replaced the `feed_search_mobile`
 * PostHog flag with a per-school setting the school's admins choose on the
 * dashboard: Off, Beta testers, or Everyone.
 *
 *  - Off: absent block, or `enabled: false`.
 *  - Beta testers: `enabled: true, betaOnly: true`.
 *  - Everyone: `enabled: true, betaOnly: false`.
 *
 * Default OFF: the block is never in the fleet-default row. `visibleForRoles`
 * and `status` are inert for this key; the dashboard stores `visibleForRoles: []`.
 * The mobile header is the only gate (`isSearchVisibleForMember`); the search
 * endpoint itself does not read this block.
 */
export const SearchFeatureConfigSchema = BaseGlobalFeatureConfigSchema

/**
 * Whether one member sees feed search: the school turned it on, and either for
 * everyone or the member is a beta tester. Absent config ⇒ off.
 */
export function isSearchVisibleForMember(
  config: Pick<z.infer<typeof SearchFeatureConfigSchema>, 'enabled' | 'betaOnly'> | null | undefined,
  member: { isBetaTester: boolean }
): boolean {
  return config?.enabled === true && (config.betaOnly !== true || member.isBetaTester)
}

// ============================================================================
// Parent AI Chat Feature Configuration
// ============================================================================

/**
 * Parent AI chat pilot (SCH-5216). A per-school setting with three states:
 *
 *  - Off: absent block, or `enabled: false`.
 *  - Beta testers: `enabled: true, betaOnly: true` (`UsersOnSchools.isBetaTester` parents).
 *  - Everyone: `enabled: true, betaOnly: false` (every parent at the school).
 *
 * Default OFF and school-only: the fleet-default row is never served as a
 * school's setting. Only super-admins may write it. The server enforces it
 * through `services/config/parent-ai-chat-audience.ts`; the app's entry point
 * only mirrors that answer. `visibleForRoles` and `status` are inert for this key.
 */
export const ParentAiChatFeatureConfigSchema = BaseGlobalFeatureConfigSchema

/**
 * Whether one member may use the parent AI chat: the school turned it on, the
 * member is a parent there (faculty who are also parents count), and either the
 * school opened it to everyone or the member is a beta tester. Absent config ⇒ off.
 */
export function isParentAiChatVisibleForMember(
  config: Pick<z.infer<typeof ParentAiChatFeatureConfigSchema>, 'enabled' | 'betaOnly'> | null | undefined,
  member: { isParent: boolean; isBetaTester: boolean }
): boolean {
  return config?.enabled === true && member.isParent && (config.betaOnly !== true || member.isBetaTester)
}

// ============================================================================
// Directory Feature Configuration
// ============================================================================

/**
 * Directory display configuration
 * Controls how user roles are presented across directory surfaces
 */
export const DirectoryFeatureConfigSchema = z.object({
  /**
   * When true, collapses "Faculty" and "Staff" role labels into "Faculty/Staff"
   * across all directory surfaces (list items, filter chips, drawer header)
   */
  mergeFacultyStaff: z.boolean().optional()
})

// ============================================================================
// Tab Bar Feature Configuration
// ============================================================================

/**
 * Tab bar visual style.
 * - `floating` — the translucent glass pill (default fleet-wide).
 * - `classic` — the edge-to-edge bar.
 *
 * An enum (not a boolean) so future bar variants stay expressible without a
 * schema-breaking change.
 */
export const TabBarStyleSchema = z.enum(['floating', 'classic'])

/**
 * Tab bar display configuration.
 * Controls which bottom tab-bar style the mobile app renders. When the whole
 * `tabBar` block is absent, mobile defaults to `floating` — so omitting it turns
 * the floating bar on. This replaced the `floating_tab_bar_mobile` PostHog flag
 * (config-over-flags: loads atomically with the rest of app config, no post-init
 * flip / flash).
 */
export const TabBarFeatureConfigSchema = z.object({
  style: TabBarStyleSchema
})

// ============================================================================
// App Availability (School Kill Switch) Feature Configuration
// ============================================================================

/**
 * Per-school mobile-app availability — the school "kill switch"
 * (plan 2026-08-18-1645, R1).
 *
 * A nested feature object rather than a bare boolean, so it fits the same
 * per-member conventions every other `globalFeatures` entry uses and survives
 * the config service's per-feature tolerant read (which copies only registered
 * members).
 *
 * NOT `BaseGlobalFeatureConfigSchema`: the base `enabled` field means "is this
 * feature switched on", which for a kill switch reads in BOTH directions — an
 * `enabled: true` that could plausibly mean "the app is enabled" or "the kill
 * switch is enabled" is exactly the ambiguity you do not want on the one control
 * that takes a school offline. `appDisabled` states the effect, not the switch.
 * There is deliberately no `visibleForRoles` either: the switch covers every
 * user of the school regardless of role (R1) — role scoping is a named non-goal.
 *
 * FAIL-SOFT BY ABSENCE (R5): the field is optional and every read site treats an
 * absent block, an absent key, and an unreadable value as "not disabled". An
 * override row is therefore never required, and no read failure can take a
 * school — or the fleet — offline.
 */
export const AppAvailabilityFeatureConfigSchema = z.object({
  /** `true` ⇒ the school's mobile app is offline for every user. Absent ⇒ available. */
  appDisabled: z.boolean().optional()
})

// ============================================================================
// Start Screen (Home Base) Feature Configuration
// ============================================================================

/**
 * The synthetic `startScreen.featureSlug` value meaning the home base is a
 * custom Page (the actual page named by `startScreen.pageSlug`, rendered by the
 * mobile `home-page` physical route) rather than a physical bottom tab. This is
 * the single source of truth every app imports — the backend write guard, the
 * web picker, and the mobile resolver/navigator all compare against it, and the
 * mobile `PHYSICAL_TAB_FEATURE_SLUGS` `as const` array MUST keep its `'home-page'`
 * literal equal to this value.
 */
export const HOME_PAGE_FEATURE_SLUG = 'home-page'

/**
 * Per-school "home base" — which screen the mobile app launches to.
 *
 * A nested feature object (NOT a bare scalar) so it fits the same per-member
 * conventions every other `globalFeatures` entry uses (`tabBar` is `{ style }`)
 * and survives the config service's per-feature tolerant read (the read path
 * copies only registered members). When the whole `startScreen` block is
 * absent, the mobile home-base resolver falls back to today's home screen — so
 * omitting it is a true no-op and the feature is purely opt-in. Absent ⇒
 * resolver default; there is deliberately NO schema-level default.
 *
 * `featureSlug` is the landable route slug the app launches to. It is EITHER a
 * physical bottom-tab `featureSlug` (e.g. `feed`, `home`, `calendar`) — the same
 * `featureSlug` the navigation tab items carry — OR the synthetic `'home-page'`
 * sentinel, used when the home base is a custom Page. In the sentinel case,
 * `pageSlug` names the Page (tab) slug the dedicated `home-page` physical route
 * renders via `usePage` + `PageRenderer`.
 *
 * Backward-compatible + additive: existing `{ featureSlug: 'feed' }` configs stay
 * valid; a page home base is `{ featureSlug: 'home-page', pageSlug: 'untitled-415l' }`.
 * Every consumer that already reads `featureSlug` keeps working — a page home base
 * simply routes to the `home-page` physical screen.
 */
export const StartScreenFeatureConfigSchema = z.object({
  /**
   * The landable route slug the app launches to: a physical tab `featureSlug`,
   * or the synthetic `'home-page'` sentinel when the home base is a custom Page.
   */
  featureSlug: z.string(),
  /** Set only when `featureSlug === 'home-page'`: the Page (tab) slug to render. */
  pageSlug: z.string().optional()
})

// ============================================================================
// Main Global Features Configuration Schema
// ============================================================================

/**
 * Complete global features configuration
 *
 * This is the root schema for the `globalFeatures` portion of app config.
 * Global features are app-wide UI elements that appear across multiple screens.
 * Each feature is optional - only include features you want to configure.
 *
 * The root object keeps Zod's default object behavior: an unknown/custom feature
 * key does not fail validation, but is stripped from the parsed output (not
 * passed through). Tightening applies to the known per-feature shapes only.
 *
 * @example
 * ```typescript
 * const globalFeatures = {
 *   chat: {
 *     enabled: true,
 *     visibleForRoles: ['student', 'parent', 'faculty', 'admin'],
 *     showBubbleOnHome: true,
 *     maxAttachmentSize: 10
 *   },
 *   'action-button': {
 *     enabled: true,
 *     visibleForRoles: ['faculty', 'admin']
 *   }
 * }
 *
 * GlobalFeaturesConfigSchema.parse(globalFeatures)
 * ```
 */
export const GlobalFeaturesConfigSchema = z.object({
  /** Chat/messaging bubble (appears across multiple screens) */
  chat: ChatFeatureConfigSchema.optional(),

  /**
   * Advanced Communications calling module (SCH-616). Absent ⇒ off. Enforced
   * server-side by `services/calls/policy/call-capability.ts` off the school's
   * own row; clients read it only to show or hide call affordances.
   */
  call: CallFeatureConfigSchema.optional(),

  /** Floating action button (appears on multiple screens) */
  'action-button': ActionButtonFeatureConfigSchema.optional(),

  /** Attendance tracking with configurable reasons and level-specific settings */
  attendance: AttendanceFeatureConfigSchema.optional(),

  /**
   * Who sees class grades (SCH-1783). Absent ⇒ off. Enforced server-side by
   * `services/config/class-grades-audience.ts`, never by parsing this blob.
   */
  classGrades: ClassGradesFeatureConfigSchema.optional(),

  /** Which grades' students have schedules shown (SCH-2501). Absent ⇒ all. School-only. */
  schedule: ScheduleFeatureConfigSchema.optional(),

  /** Feed search in the mobile app. Absent ⇒ off. */
  search: SearchFeatureConfigSchema.optional(),

  /**
   * Parent AI chat pilot (SCH-5216). Absent ⇒ off. School-only, super-admin
   * writes. Enforced server-side by `services/config/parent-ai-chat-audience.ts`.
   */
  parentAiChat: ParentAiChatFeatureConfigSchema.optional(),

  /** Directory display configuration (role label presentation, filter options) */
  directory: DirectoryFeatureConfigSchema.optional(),

  /** Resource board layout: tiles per row (SCH-3172). Absent ⇒ 1 column. */
  resourceBoard: ResourceBoardFeatureConfigSchema.optional(),

  /** Bottom tab-bar style (floating glass pill vs classic bar). Absent ⇒ floating. */
  tabBar: TabBarFeatureConfigSchema.optional(),

  /** Home base — which tab the app launches to. Absent ⇒ resolver default (home). */
  startScreen: StartScreenFeatureConfigSchema.optional(),

  /**
   * Per-school mobile-app availability (the kill switch). Absent ⇒ available.
   * Read by `services/config/school-app-availability.ts`, never by parsing this
   * whole blob — see that module's header.
   */
  'app-availability': AppAvailabilityFeatureConfigSchema.optional(),

  /**
   * Resolved per-school media-quality policy (tier + photo/video caps).
   * Injected at app-config assembly by the media-quality resolver (SCH-136);
   * never persisted as a school override. Absent on stale cached payloads —
   * mobile falls back to `standard` caps.
   */
  'media-quality': MediaQualityPolicySchema.optional()
})

// ============================================================================
// TypeScript Types
// ============================================================================

/**
 * Infer TypeScript types from the Zod schemas
 * Use these types in your TypeScript code
 */
export type BaseGlobalFeatureConfig = z.infer<typeof BaseGlobalFeatureConfigSchema>
export type SingleGlobalFeatureConfig = z.infer<typeof SingleGlobalFeatureConfigSchema>
export type ChatRole = z.infer<typeof ChatRoleSchema>
export type ChatRoleOrAll = z.infer<typeof ChatRoleOrAllSchema>
export type ChatRecipientRole = z.infer<typeof ChatRecipientRoleSchema>
export type ChatInitiationRules = z.infer<typeof ChatInitiationRulesSchema>
export type ChatFeatureConfig = z.infer<typeof ChatFeatureConfigSchema>
export type CallFeatureConfig = z.infer<typeof CallFeatureConfigSchema>
export type CallRecordingConfig = z.infer<typeof CallRecordingConfigSchema>
export type ChatFeatureConfigWrite = z.infer<typeof ChatFeatureConfigWriteSchema>
export type ActionButtonFeatureConfig = z.infer<typeof ActionButtonFeatureConfigSchema>
export type AttendanceReasonOption = z.infer<typeof AttendanceReasonOptionSchema>
export type AttendanceBehaviorConfig = z.infer<typeof AttendanceBehaviorConfigSchema>
export type AttendanceLevelConfig = z.infer<typeof AttendanceLevelConfigSchema>
export type ParentAbsenceType = z.infer<typeof ParentAbsenceTypeSchema>
export type ParentAbsenceLevelTypeLists = z.infer<typeof ParentAbsenceLevelTypeListsSchema>
export type ParentAbsenceLevelReasons = z.infer<typeof ParentAbsenceLevelReasonsSchema>
export type ParentAbsenceReasonsByLevel = z.infer<typeof ParentAbsenceReasonsByLevelSchema>
export type ParentAbsenceRequestTypesByLevel = z.infer<typeof ParentAbsenceRequestTypesByLevelSchema>
export type ParentAbsenceResolvedReasonsByLevel = z.infer<typeof ParentAbsenceResolvedReasonsByLevelSchema>
export type ParentAbsenceResolvedReasonsByLevelAndType = z.infer<
  typeof ParentAbsenceResolvedReasonsByLevelAndTypeSchema
>
export type ParentAbsenceConfig = z.infer<typeof ParentAbsenceConfigSchema>
export type TeacherAttendanceState = z.infer<typeof TeacherAttendanceStateSchema>
export type AttendanceFeatureConfig = z.infer<typeof AttendanceFeatureConfigSchema>
export type ClassGradesFeatureConfig = z.infer<typeof ClassGradesFeatureConfigSchema>
export type ClassGradesFeatureConfigWrite = z.infer<typeof ClassGradesFeatureConfigWriteSchema>
export type ScheduleFeatureConfig = z.infer<typeof ScheduleFeatureConfigSchema>
export type ScheduleFeatureConfigWrite = z.infer<typeof ScheduleFeatureConfigWriteSchema>
export type SearchFeatureConfig = z.infer<typeof SearchFeatureConfigSchema>
export type ParentAiChatFeatureConfig = z.infer<typeof ParentAiChatFeatureConfigSchema>
export type DirectoryFeatureConfig = z.infer<typeof DirectoryFeatureConfigSchema>
export type ResourceBoardColumns = z.infer<typeof ResourceBoardColumnsSchema>
export type ResourceBoardFeatureConfig = z.infer<typeof ResourceBoardFeatureConfigSchema>
export type TabBarStyle = z.infer<typeof TabBarStyleSchema>
export type TabBarFeatureConfig = z.infer<typeof TabBarFeatureConfigSchema>
export type StartScreenFeatureConfig = z.infer<typeof StartScreenFeatureConfigSchema>
export type AppAvailabilityFeatureConfig = z.infer<typeof AppAvailabilityFeatureConfigSchema>
export type GlobalFeaturesConfig = z.infer<typeof GlobalFeaturesConfigSchema>

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Validate global features configuration and return typed result
 *
 * @param data - Raw global features configuration data
 * @returns Validated and typed global features configuration
 * @throws ZodError if validation fails
 */
export function validateGlobalFeaturesConfig(data: unknown): GlobalFeaturesConfig {
  return GlobalFeaturesConfigSchema.parse(data)
}

/**
 * Safely parse global features configuration without throwing
 *
 * @param data - Raw global features configuration data
 * @returns Success or error result
 */
export function safeParseGlobalFeaturesConfig(data: unknown) {
  return GlobalFeaturesConfigSchema.safeParse(data)
}

/**
 * Check if a feature is enabled in the configuration
 *
 * @param config - Global features configuration
 * @param featureName - Name of the feature to check
 * @returns Whether the feature is enabled
 */
export function isFeatureEnabled(config: GlobalFeaturesConfig, featureName: keyof GlobalFeaturesConfig): boolean {
  const feature = config[featureName]
  return feature ? (feature as { enabled?: boolean }).enabled === true : false
}
